import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import AutoCompletedInput from 'core/components/AutoComplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  AddressDTO,
} from '@/dtos';
import isNil from 'lodash/isNil';
import {
  autosuggestHighlightParse,
} from 'utils/misc';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import {
  Controller,
  useForm,
} from 'react-hook-form';
import {
  useGoogleMapGeoCode,
  useGetGooglePlaces,
} from 'components/hooks';
import debounce from 'lodash/debounce';

export type Place = Partial<google.maps.places.AutocompletePrediction> | null;

type Props = {
  onChange?: (value: AddressDTO, event?: ChangeEvent<HTMLInputElement>) => void;
  value?: Place;
  onBlur?: () => void;
};

export default function AddressSuggestion({ onChange, value, onBlur }: Props) {
  const [address, setAddress] = useState<string | undefined>('');
  const [inputValue, setInputValue] = useState('');

  const { control, setValue } = useForm();
  const { data: options } = useGetGooglePlaces(inputValue);

  const { data, error: geoCodeError } = useGoogleMapGeoCode(address ?? '');

  useEffect(() => {
    if (isNil(geoCodeError) && !isNil(data)) {
      onChange?.(data);
    }
  }, [data]);

  useEffect(() => {
    if (!isNil(value)) {
      setValue('place', value);
    }
  }, [value]);

  const onInputChange = useCallback(debounce((
    event: SyntheticEvent,
    value: string,
  ) => {
    if (!isNil(event)) {
      setInputValue(value);
    }
  }, 700), []);

  return (
    <Controller
      name="place"
      control={control}
      render={({ field: { value } }) => (
        <AutoCompletedInput<Place>
          id="address-suggestion"
          className="w-full !pointer-events-auto"
          onBlur={onBlur}
          getLabel={(option) => (option?.description ?? '')}
          options={options ?? []}
          filterOptions={(x) => x}
          clearOnBlur={false}
          value={value ?? null}
          filterSelectedOptions
          noOptionsText="No locations"
          placeholder="Enter Address"
          clearIcon={<CloseIcon className="!h-4 !w-4 -m-0.5" />}
          dropdownClassName={clsx('!pointer-events-auto', {
            '!p-2': (options ?? []).length === 0,
          })}
          onChange={(value) => {
            const newValue = value as Place;
            setValue('place', newValue);
            setAddress(newValue?.description);
          }}
          onInputChange={onInputChange}
          renderOption={(props, option) => {
            // MUI includes `key` in this props object. React requires `key` be passed
            // directly, not via spread.
            const { key: muiKey, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & {
              key: React.Key;
            };
            const matches = option?.structured_formatting?.main_text_matched_substrings ?? [];
            const parts = autosuggestHighlightParse(
              option?.structured_formatting?.main_text ?? '',
              matches.map((match) => [match.offset, match.offset + match.length]),
            );
            return (
              <li
                key={option?.place_id ?? option?.description ?? muiKey}
                {...liProps}
                className={clsx(
                  'flex items-center hover:bg-gray-100 !text-xs cursor-pointer',
                  liProps.className,
                )}
              >
                <div className="mx-1">
                  <LocationOnIcon className="text-gray-400 !text-base" />
                </div>
                <div className="w-[calc(100%-44px)]">
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      className={clsx({ 'font-semibold': part.highlight })}
                    >
                      {part.text}
                    </span>
                  ))}
                  <p className=" text-gray-400">
                    {option?.structured_formatting?.secondary_text}
                  </p>
                </div>
              </li>
            );
          }}
        />
      )}
    />
  );
}
